import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import App from './App.vue';
import router from './router';
import i18n, { setLanguage } from './i18n';
import AlertNotification from './components/ui/AlertNotification.vue';
import { useAuthStore } from './store/auth';
import { identifyChatwootUser, startChatwoot } from './utils/chatwoot';
import {
  betaSiteDestination,
  shouldRedirectToBetaSite,
} from './utils/betaSite';

// Import both styles (Bulma with old import, our styles with new @use)
import './styles/main-bulma.scss';
import './styles/main.scss';

// Honor a stored beta choice before the app mounts so the current
// site does not flash. ?nobeta=1 clears that choice on any route.
const redirectingToBeta = shouldRedirectToBetaSite(window.location.search);
if (redirectingToBeta) {
  window.location.replace(betaSiteDestination());
}

// Create app
const app = createApp(App);

// Add global properties
app.provide('APP_NAME', 'Indiefy');
app.provide('APP_VERSION', '1.0.0');

// Use plugins
const pinia = createPinia();
const head = createHead();
app.use(pinia);
app.use(head);
app.use(router);
app.use(i18n);

// Register global components
app.component('AlertNotification', AlertNotification);

// Check for stored language, query parameter, or use browser language
const supportedLanguages = ['en', 'es', 'pt', 'tr', 'hi', 'fr'];
const storedLanguage = localStorage.getItem('indiefy-language');
const urlParams = new URLSearchParams(window.location.search);
const langParam = urlParams.get('lang');

if (storedLanguage) {
  setLanguage(storedLanguage);
} else if (langParam) {
  setLanguage(langParam);
} else {
  // Get browser language and clean it up
  let browserLang = window.navigator.userLanguage || window.navigator.language;
  browserLang = browserLang.split('-')[0].split('_')[0].toLowerCase();

  // Use browser language if supported, otherwise fallback to English
  const lang = supportedLanguages.includes(browserLang) ? browserLang : 'en';
  setLanguage(lang);
}

// Initialize theme from localStorage synchronously to prevent FOUC.
// Dark mode is a PRO-only feature, so we also check the cached user payload
// in localStorage before applying the classes. Otherwise a non-PRO user with
// a stale `indiefy-theme=dark` (e.g. after their subscription lapsed) would
// see the page render dark and then flash back to light once `App.vue`'s
// `themeStore.setDarkMode(true)` runs on mount.
const savedTheme = localStorage.getItem('indiefy-theme');
if (savedTheme === 'dark') {
  let canUseDarkMode = false;
  try {
    const storedUser = JSON.parse(localStorage.getItem('indiefy-user'));
    canUseDarkMode = !!(storedUser && storedUser.subscription);
  } catch (e) {
    canUseDarkMode = false;
  }

  if (canUseDarkMode) {
    document.documentElement.classList.add('dark-theme');
    document.body.classList.add('body--dark');
  } else {
    // Clear the stale preference so the store and storage stay in sync.
    localStorage.setItem('indiefy-theme', 'light');
  }
}

// Initialize authentication state
// The auth store's state initializer already reads `indiefy-user` from
// localStorage synchronously, so `user` and `isAuthenticated` are populated
// before the first router guard runs. This function starts a server refresh
// early so the router guard (which awaits `verifySession()` on every page)
// can join the same in-flight request. Magic-link arrivals also hydrate here.
//
// Chatwoot is identified from the cached user immediately. Waiting for
// `verifySession()` caused a race: the widget often booted anonymous,
// then `setUser` either never ran (SDK not ready yet) or reset the
// widget after the page already looked finished.
const initializeAuth = async () => {
  try {
    const authStore = useAuthStore();

    const hasStoredUser = !!authStore.user;
    const isMagicLink = new URLSearchParams(window.location.search)
      .get('magic_link') === '1';

    if (authStore.user) {
      identifyChatwootUser(authStore.user);
    } else {
      startChatwoot();
    }

    if (hasStoredUser || isMagicLink) {
      if (hasStoredUser) {
        // Kick off the refresh immediately so the router guard can
        // await the same in-flight `verifySession()` instead of waiting
        // until after this function yields. `initializing` is cleared
        // in `verifySession()`'s finally.
        authStore.initializing = false;
        authStore.verifySession();
      } else {
        await authStore.verifySession();
      }
    } else {
      authStore.initializing = false;
    }
  } catch (error) {
    console.error('Error initializing authentication:', error);
    try {
      useAuthStore().initializing = false;
    } catch (storeError) {
      console.error('Error accessing auth store:', storeError);
    }
  }
};

// A stored beta choice is already leaving this page. Skip session
// refresh and mount so the redirect is not racing the app.
if (!redirectingToBeta) {
  // Start the session refresh in parallel with the first route resolving
  // instead of waiting until after mount. The router guard awaits the
  // same in-flight `verifySession()` promise on every page.
  initializeAuth();

  // Wait for the router to resolve the initial route (including async guards
  // and dynamic component imports) before mounting. This prevents the
  // "blink twice" effect where `<router-view>` first renders empty (no
  // component resolved yet) and then re-renders with the resolved component,
  // triggering the page transition's enter animation a second time.
  router.isReady().then(() => {
    app.mount('#app');
  });
}